import type { PassportEcosystemResult } from '@bilt/types';
/**
 * Bilt Passport — web API wrappers.
 *
 * Passport program keys are **shipped** (always on) unless an env emergency
 * override disables them (`PASSPORT_FLAG_*=0`). See docs/FEATURE_FLAGS.md.
 * Client bootstrap treats missing Passport keys as on so a stale/offline
 * client_config cannot hide production Passport UX.
 *
 * Product tone: QR collection and confirmed attendance carry distinct evidence. Never introduce XP/points/leaderboard language around these types.
 */
import apiService from '../ApiService';
import type { PassportCard } from './passportCards';

export type PassportEncounterStatus = 'pending' | 'verified' | 'rejected' | 'revoked';

export type PassportVerificationLevel =
  | 'basic_qr'
  | 'time_verified'
  | 'event_verified'
  | 'proximity_verified'
  | 'staff_verified'
  | 'offline_pending'
  | 'rejected_suspicious';

export type PassportRiskStatus = 'clear' | 'flagged' | 'held';

export type PassportSubjectType =
  | 'vehicle'
  | 'event'
  | 'shop'
  | 'club'
  | 'booth'
  | 'activation'
  | 'destination'
  | 'checkpoint'
  | 'place'
  | 'commemorative';

export type PassportEncounterMethod =
  'qr_scan' | 'nfc_tap' | 'manual_code' | 'geofence_checkin' | 'organizer_grant' | 'system_grant';

export interface PassportFeatureFlags {
  passport_enabled: boolean;
  vehicle_encounters_enabled: boolean;
  event_encounters_enabled: boolean;
  passport_collections_enabled: boolean;
  shop_encounters_enabled: boolean;
  club_encounters_enabled: boolean;
  sponsor_campaigns_enabled: boolean;
  passport_rewards_enabled: boolean;
  passport_location_verification_enabled: boolean;
  passport_offline_scans_enabled: boolean;
  passport_public_profiles_enabled: boolean;
}

/** Shipped Passport posture: missing client_config keys resolve ON. */
const SHIPPED_PASSPORT_FLAGS: PassportFeatureFlags = {
  passport_enabled: true,
  vehicle_encounters_enabled: true,
  event_encounters_enabled: true,
  passport_collections_enabled: true,
  shop_encounters_enabled: true,
  club_encounters_enabled: true,
  sponsor_campaigns_enabled: true,
  passport_rewards_enabled: true,
  passport_location_verification_enabled: true,
  passport_offline_scans_enabled: true,
  passport_public_profiles_enabled: true,
};

function readPassportFlag(flat: Record<string, boolean>, key: keyof PassportFeatureFlags): boolean {
  const flatKey = `passport.${key}`;
  if (Object.prototype.hasOwnProperty.call(flat, flatKey)) {
    return Boolean(flat[flatKey]);
  }
  return SHIPPED_PASSPORT_FLAGS[key];
}

export interface PassportSubjectPreview {
  /** Permission-filtered card metadata, independent of immutable issuance evidence. */
  card_summary?: {
    badges?: import('../../types/discoveryBadges').IssuedBadge[];
    badge_overflow?: number;
    limited_designation?: {
      public_id: string;
      name: string;
      ordinal: number;
      edition_size: number;
      issuer_label?: string;
      ordinal_label?: string;
      public_blurb?: string | null;
    } | null;
  };
  /** Normalized source photograph focal point; never overrides published artwork. */
  focal_x?: number | null;
  focal_y?: number | null;
  subject_ref?: string;
  facts?: Record<string, string | number | null>;
  subject_type: PassportSubjectType;
  subject_id: number;
  title: string;
  subtitle?: string | null;
  thumbnail_url?: string | null;
  public_slug?: string | null;
  owner_username?: string | null;
  /** Additive list enrichment (vehicle collectible continuity). */
  image_url?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  modification_count?: number | null;
  certification_status?: string | null;
  rarity_stars?: number | null;
  collectible?: {
    kind?: string;
    subtitle?: string | null;
    logo_url?: string | null;
    cover_url?: string | null;
    campaign_title?: string | null;
    object_kind?: string | null;
    title?: string;
    year?: number | null;
    make?: string | null;
    model?: string | null;
    trim?: string | null;
    image_url?: string | null;
    modification_count?: number;
    certification_status?: string | null;
    rarity_stars?: number | null;
    username?: string | null;
    slug?: string | null;
  } | null;
}

export interface PassportEncounter {
  id: number;
  public_id: string;
  vehicle_id: number | null;
  subject_type: PassportSubjectType;
  subject_id: number;
  subject?: PassportSubjectPreview;
  campaign_id: number | null;
  method: PassportEncounterMethod;
  occurred_at: string;
  recorded_at: string;
  status: PassportEncounterStatus;
  verification_level: PassportVerificationLevel;
  risk_status: PassportRiskStatus;
  memory_caption?: string | null;
  memory_visibility?: 'private' | 'public' | null;
  media_count?: number;
}

export interface PassportEncounterMedia {
  id: number;
  media_type: 'image' | 'video';
  storage_path: string;
  thumbnail_path?: string | null;
  width?: number;
  height?: number;
  sort_order?: number;
  created_at?: string;
}

export interface PassportEncounterDetail extends PassportEncounter {
  media?: PassportEncounterMedia[];
}

export interface PassportSummary {
  encounter_count: number;
  verified_encounter_count: number;
  pending_encounter_count: number;
  vehicles_encountered_count: number;
  events_encountered_count: number;
  collections_in_progress: number;
  collections_completed: number;
  campaigns_joined: number;
  campaigns_completed: number;
  recent_encounters: PassportEncounter[];
}

export interface VehiclePassportStats {
  vehicle_id?: number;
  public_count_enabled: boolean;
  seen_in_person_count: number;
  qr_encounter_count?: number;
}

export interface PassportCollectionRequirement {
  requirement_key: string | null;
  title?: string | null;
  description?: string | null;
  hidden: boolean;
  satisfied?: boolean;
}

export interface PassportCollectionDefinition {
  id: number;
  public_id: string;
  slug: string;
  collection_type: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  visibility: 'public' | 'hidden' | 'limited';
  owner_type: string;
  requirements?: PassportCollectionRequirement[];
  progress?: { percent_complete: number; status: string } | null;
}

export interface PassportCampaignObject {
  id?: number;
  public_id: string;
  object_kind?: 'booth' | 'activation' | 'destination' | 'checkpoint';
  title?: string;
  description?: string | null;
  sequence_order?: number | null;
  location_label?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
  location_radius_m?: number | null;
  is_required?: boolean;
  is_hidden?: boolean;
  /** True when the server withheld title/location for reveal rules. */
  hidden?: boolean;
  /** True when this user has a verified encounter at this object. */
  visited?: boolean;
}

export interface PassportCampaignSummary {
  id: number;
  public_id: string;
  slug: string;
  campaign_type: string;
  owner_type: string;
  sponsor_name?: string | null;
  title: string;
  description?: string | null;
  hero_media_path?: string | null;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'ended' | 'archived';
  starts_at?: string | null;
  ends_at?: string | null;
  is_public: boolean;
  required_objects_total?: number;
  objects?: PassportCampaignObject[];
  participation?: {
    status: string;
    objects_visited_count: number;
    required_objects_total: number;
  } | null;
}

export interface PassportScanResult {
  ecosystem?: PassportEcosystemResult;
  card_outcomes?: Array<{ title: string; message: string }>;
  cards?: PassportCard[];
  /** Null only for the `owner_self` case — you can't log an encounter with your own build. */
  encounter_id: number | null;
  public_id: string | null;
  status: PassportEncounterStatus | null;
  verification_level?: PassportVerificationLevel | null;
  subject: PassportSubjectPreview | null;
  dedupe_outcome: 'new' | 'already_recorded';
  owner_self?: boolean;
}

/**
 * Admin encounter row shape from `passportAdminListEncounters()` — same fields as
 * `PassportEncounter` (no embedded subject/scanner preview or risk reasons yet;
 * the admin facade returns bare encounter rows).
 */
export type PassportReviewQueueItem = PassportEncounter;

export interface PassportScanAppeal {
  public_id: string;
  encounter_id: number;
  appeal_reason: string;
  status: 'open' | 'under_review' | 'upheld_rejection' | 'overturned_approved' | 'withdrawn';
  created_at: string;
  resolved_at?: string | null;
  resolution_notes?: string | null;
}

/**
 * Reads the flattened `feature_flags` map from `client_config.php`.
 * Passport keys are shipped: missing entries default to on. Only an explicit
 * false from the server (env emergency off) disables UX.
 */
export async function getPassportFlags(): Promise<PassportFeatureFlags> {
  try {
    const response = await apiService.makeRequest<{ feature_flags?: Record<string, boolean> }>(
      '/client_config.php',
      { method: 'GET' }
    );
    const flat = response.data?.feature_flags || {};
    return {
      passport_enabled: readPassportFlag(flat, 'passport_enabled'),
      vehicle_encounters_enabled: readPassportFlag(flat, 'vehicle_encounters_enabled'),
      event_encounters_enabled: readPassportFlag(flat, 'event_encounters_enabled'),
      passport_collections_enabled: readPassportFlag(flat, 'passport_collections_enabled'),
      shop_encounters_enabled: readPassportFlag(flat, 'shop_encounters_enabled'),
      club_encounters_enabled: readPassportFlag(flat, 'club_encounters_enabled'),
      sponsor_campaigns_enabled: readPassportFlag(flat, 'sponsor_campaigns_enabled'),
      passport_rewards_enabled: readPassportFlag(flat, 'passport_rewards_enabled'),
      passport_location_verification_enabled: readPassportFlag(
        flat,
        'passport_location_verification_enabled'
      ),
      passport_offline_scans_enabled: readPassportFlag(flat, 'passport_offline_scans_enabled'),
      passport_public_profiles_enabled: readPassportFlag(flat, 'passport_public_profiles_enabled'),
    };
  } catch {
    return { ...SHIPPED_PASSPORT_FLAGS };
  }
}

export function isPassportEnabled(flags: PassportFeatureFlags | null | undefined): boolean {
  // Shipped: treat missing bootstrap as on.
  if (!flags) return true;
  return flags.passport_enabled !== false;
}

/** Raw shape returned by `passportSummary()` in `backend/includes/passport_utils.php`. */
interface PassportSummaryApiShape {
  pending_encounters?: number;
  total_encounters: number;
  encounters_by_subject_type: Partial<Record<PassportSubjectType, number>>;
  collections_in_progress: number;
  collections_completed: number;
  campaigns_joined: number;
  campaigns_completed: number;
  recent_encounters: PassportEncounter[];
}

export async function getPassportSummary(): Promise<PassportSummary> {
  const response = await apiService.makeRequest<PassportSummaryApiShape>('/passport_summary.php', {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load Passport summary');
  }
  const raw = response.data;
  const bySubject = raw.encounters_by_subject_type || {};
  return {
    // Backend currently only counts verified encounters here; there is no
    // separate all-statuses total, so we report the verified count for both.
    encounter_count: raw.total_encounters,
    verified_encounter_count: raw.total_encounters,
    pending_encounter_count: raw.pending_encounters ?? 0,
    vehicles_encountered_count: bySubject.vehicle || 0,
    events_encountered_count: bySubject.event || 0,
    collections_in_progress: raw.collections_in_progress,
    collections_completed: raw.collections_completed,
    campaigns_joined: raw.campaigns_joined,
    campaigns_completed: raw.campaigns_completed,
    recent_encounters: raw.recent_encounters || [],
  };
}

export interface PassportEncounterFilters {
  feed_category?: string;
  subject_type?: PassportSubjectType;
  vehicle_id?: number;
  status?: PassportEncounterStatus;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

export async function listPassportEncounters(
  filters: PassportEncounterFilters = {}
): Promise<{ data: PassportEncounter[]; total_pages: number; page: number }> {
  const { page, limit, ...rest } = filters;
  const pageNum = page && page > 0 ? page : 1;
  const limitNum = limit && limit > 0 ? limit : 20;
  const qs = new URLSearchParams();
  Object.entries(rest).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  qs.set('limit', String(limitNum));
  qs.set('offset', String((pageNum - 1) * limitNum));
  const response = await apiService.makeRequest<PassportEncounter[]>(
    `/passport_encounters.php?${qs}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load your Passport timeline');
  }
  const pagination = (
    response as unknown as { pagination?: { total?: number; limit?: number; offset?: number } }
  ).pagination;
  const total = pagination?.total ?? 0;
  const effectiveLimit = pagination?.limit ?? limitNum;
  return {
    data: Array.isArray(response.data) ? response.data : [],
    total_pages: effectiveLimit > 0 ? Math.max(1, Math.ceil(total / effectiveLimit)) : 1,
    page: pageNum,
  };
}

/** Raw shape returned by `passportEncounterDetail()` — memory is nested, not flattened. */
interface PassportEncounterDetailApiShape extends Omit<
  PassportEncounter,
  'memory_caption' | 'memory_visibility'
> {
  memory: { caption: string | null; visibility: 'private' | 'public'; updated_at: string } | null;
  media?: PassportEncounterDetail['media'];
  subject?: PassportSubjectPreview & { username?: string | null; slug?: string | null };
}

export async function getPassportEncounterDetail(
  publicId: string
): Promise<PassportEncounterDetail> {
  const response = await apiService.makeRequest<PassportEncounterDetailApiShape>(
    `/passport_encounter_detail.php?public_id=${encodeURIComponent(publicId)}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Encounter not found');
  }
  const raw = response.data;
  const { memory, subject, ...rest } = raw;
  return {
    ...rest,
    memory_caption: memory?.caption ?? null,
    memory_visibility: memory?.visibility ?? null,
    subject: subject
      ? {
          ...subject,
          public_slug: subject.public_slug ?? subject.slug,
          owner_username: subject.owner_username ?? subject.username,
        }
      : undefined,
  };
}

export async function savePassportEncounterMemory(
  publicId: string,
  payload: { caption?: string; visibility?: 'private' | 'public' }
): Promise<void> {
  const response = await apiService.makeRequest('/passport_encounter_memory.php', {
    method: 'POST',
    body: JSON.stringify({ encounter_public_id: publicId, ...payload }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to save memory');
  }
}

export async function listPassportEncounterMedia(
  encounterPublicId: string
): Promise<PassportEncounterMedia[]> {
  const response = await apiService.makeRequest<PassportEncounterMedia[]>(
    `/passport_encounter_media.php?encounter_public_id=${encodeURIComponent(encounterPublicId)}`,
    { headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.error || 'Failed to load encounter media');
  }
  return response.data ?? [];
}

export async function uploadPassportEncounterMedia(
  encounterPublicId: string,
  file: File
): Promise<PassportEncounterMedia> {
  const formData = new FormData();
  formData.append('encounter_public_id', encounterPublicId);
  formData.append('image', file);
  const response = await apiService.makeRequest<PassportEncounterMedia>(
    '/passport_encounter_media.php',
    {
      method: 'POST',
      body: formData,
      headers: apiService.getAuthHeaders(),
    }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to upload encounter media');
  }
  if (!response.data?.id) {
    throw new Error('Upload succeeded but media payload was incomplete');
  }
  return response.data;
}

export async function fetchPassportEncounterMediaBlob(
  encounterPublicId: string,
  mediaId: number
): Promise<Blob> {
  const headers = apiService.getAuthHeaders() as Record<string, string>;
  const res = await fetch(
    `${apiService.baseUrl}/passport_encounter_media.php?encounter_public_id=${encodeURIComponent(encounterPublicId)}&media_id=${mediaId}`,
    { headers, credentials: 'include' }
  );
  if (!res.ok) {
    throw new Error('Could not load photo');
  }
  return res.blob();
}

export async function deletePassportEncounterMedia(
  encounterPublicId: string,
  mediaId: number
): Promise<void> {
  const response = await apiService.makeRequest('/passport_encounter_media.php', {
    method: 'DELETE',
    body: JSON.stringify({ encounter_public_id: encounterPublicId, media_id: mediaId }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.error || 'Failed to delete encounter media');
  }
}

export async function submitPassportScanAppeal(
  publicId: string,
  appealReason: string
): Promise<void> {
  const response = await apiService.makeRequest('/passport_scan_appeal.php', {
    method: 'POST',
    body: JSON.stringify({ encounter_public_id: publicId, appeal_reason: appealReason }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to submit appeal');
  }
}

export interface PassportScanSubmission {
  payload: string;
  method: 'manual_code' | 'qr_scan';
  location?: { lat: number; lng: number; accuracy_m?: number; timestamp: number };
  vehicle_id?: number;
  client_scan_id?: string;
}

/** Raw shape from `passport_scan.php` — encounter/subject are nested, and `duplicate` is a boolean. */
interface PassportScanApiShape {
  ecosystem?: PassportEcosystemResult;
  card_outcomes?: Array<{ title: string; message: string }>;
  cards?: PassportCard[];
  encounter: PassportEncounter | null;
  duplicate?: boolean;
  owner_self?: boolean;
  subject?: PassportSubjectPreview | null;
}

function passportScanResultFromRaw(raw: PassportScanApiShape): PassportScanResult {
  return {
    ecosystem: raw.ecosystem,
    cards: raw.cards || [],
    card_outcomes: raw.card_outcomes || [],
    encounter_id: raw.encounter?.id ?? null,
    public_id: raw.encounter?.public_id ?? null,
    status: raw.encounter?.status ?? null,
    verification_level: raw.encounter?.verification_level ?? null,
    subject: raw.subject ?? null,
    dedupe_outcome: raw.duplicate ? 'already_recorded' : 'new',
    owner_self: Boolean(raw.owner_self),
  };
}

/** Safe public preview for a scan payload. Never creates an encounter. */
export async function previewPassportScan(payload: string): Promise<{
  subject?: PassportSubjectPreview | null;
  token_kind?: string;
  requires_location?: boolean;
} | null> {
  const qs = new URLSearchParams({ payload });
  const response = await apiService.makeRequest<{
    subject?: PassportSubjectPreview;
    token_kind?: string;
    requires_location?: boolean;
  }>(`/passport_scan_token_preview.php?${qs}`, { method: 'GET' });
  if (!response.success || !response.data) {
    return null;
  }
  return response.data;
}

/** Web manual code / paste URL scan — a secondary path, not the primary mobile camera flow. */
export async function submitPassportScan(
  payload: PassportScanSubmission
): Promise<PassportScanResult> {
  try {
    const response = await apiService.makeRequest<PassportScanApiShape>('/passport_scan.php', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: apiService.getAuthHeaders(),
    });
    if (!response.success || !response.data) {
      throw new Error(response.message || response.error || 'Could not log that encounter');
    }
    return passportScanResultFromRaw(response.data);
  } catch (err: unknown) {
    // A `dedupe_conflict` (HTTP 409) is not a failure: the encounter already exists and the
    // body names it. Return it as a duplicate result so the caller can link to the record
    // the collector already has, instead of a dead-end "already logged" message.
    const failure = err as { code?: string; data?: PassportScanApiShape } | null;
    if (failure?.code === 'dedupe_conflict') {
      return passportScanResultFromRaw({
        encounter: failure.data?.encounter ?? null,
        duplicate: true,
        subject: failure.data?.subject ?? failure.data?.encounter?.subject ?? null,
      });
    }
    throw err;
  }
}

/** Raw shape from `passportVehiclePublicCounts()` — "strong" is event/proximity/staff verified only. */
interface VehiclePassportStatsApiShape {
  public_count_enabled: boolean;
  seen_in_person_strong?: number;
  qr_encounters_basic?: number;
}

/** Public, opt-in aggregate for a vehicle's own page. Never exposes scanner identities. */
export async function getVehiclePassportStats(vehicleId: number): Promise<VehiclePassportStats> {
  const response = await apiService.makeRequest<VehiclePassportStatsApiShape>(
    `/passport_vehicle_stats.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`,
    { method: 'GET' }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Passport stats unavailable');
  }
  const raw = response.data;
  return {
    vehicle_id: vehicleId,
    public_count_enabled: raw.public_count_enabled,
    seen_in_person_count: raw.seen_in_person_strong ?? 0,
    qr_encounter_count: raw.qr_encounters_basic ?? 0,
  };
}

export async function listPassportCollections(
  filters: { collection_type?: string; owner_type?: string } = {}
): Promise<PassportCollectionDefinition[]> {
  const qs = new URLSearchParams();
  if (filters.collection_type) qs.set('collection_type', filters.collection_type);
  if (filters.owner_type) qs.set('owner_type', filters.owner_type);
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await apiService.makeRequest<PassportCollectionDefinition[]>(
    `/collections_list.php${suffix}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load collections');
  }
  return Array.isArray(response.data) ? response.data : [];
}

/** Raw shape from `passportCollectionDetail()` — definition/requirements/progress are nested. */
interface PassportCollectionDetailApiShape {
  definition: PassportCollectionDefinition;
  version: { id: number; version_number: number; published_at: string } | null;
  requirements: PassportCollectionRequirement[];
  progress: { percent_complete: number; status: string; satisfied_requirements?: unknown[] } | null;
}

export async function getPassportCollectionDetail(
  slug: string,
  vehicleId?: number
): Promise<PassportCollectionDefinition> {
  const qs = new URLSearchParams({ slug });
  if (vehicleId) qs.set('vehicle_id', String(vehicleId));
  const response = await apiService.makeRequest<PassportCollectionDetailApiShape>(
    `/collection_detail.php?${qs.toString()}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Collection not found');
  }
  const raw = response.data;
  return {
    ...raw.definition,
    requirements: raw.requirements,
    progress: raw.progress
      ? { percent_complete: raw.progress.percent_complete, status: raw.progress.status }
      : null,
  };
}

export async function listPassportCampaigns(
  filters: { campaign_type?: string; owner_type?: string } = {}
): Promise<PassportCampaignSummary[]> {
  const qs = new URLSearchParams();
  if (filters.campaign_type) qs.set('campaign_type', filters.campaign_type);
  if (filters.owner_type) qs.set('owner_type', filters.owner_type);
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await apiService.makeRequest<PassportCampaignSummary[]>(
    `/campaigns_list.php${suffix}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load campaigns');
  }
  return Array.isArray(response.data) ? response.data : [];
}

/** Raw shape from `passportCampaignDetail()` — campaign/objects/participant are nested. */
interface PassportCampaignDetailApiShape {
  campaign: PassportCampaignSummary;
  objects: PassportCampaignObject[];
  rewards?: unknown[];
  participant: {
    status: string;
    objects_visited_count: number;
    required_objects_total: number;
  } | null;
}

export async function getPassportCampaignDetail(slug: string): Promise<PassportCampaignSummary> {
  const response = await apiService.makeRequest<PassportCampaignDetailApiShape>(
    `/campaign_detail.php?slug=${encodeURIComponent(slug)}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Campaign not found');
  }
  const raw = response.data;
  return {
    ...raw.campaign,
    objects: raw.objects,
    participation: raw.participant,
  };
}

export async function joinPassportCampaign(payload: {
  campaign_id: number;
  vehicle_id?: number;
  legal_accepted?: boolean;
  legal_terms_version?: string;
}): Promise<void> {
  const response = await apiService.makeRequest('/campaign_join.php', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to join campaign');
  }
}

// —— Admin moderation (super_admin only; admin/passport.php action-based facade) ——
// Contract: action=flags|set_flag|encounters|review|revoke_token|campaigns|
// campaign_save|campaign_object_add|collections|collection_save|
// collection_publish_version|appeal_resolve|health. See
// docs/BILT_PASSPORT_API_ARCHITECTURE.md §3.

const ADMIN_ENDPOINT = '/admin/passport.php';

function adminPassportUrl(
  action: string,
  params: Record<string, string | number | undefined> = {}
): string {
  const qs = new URLSearchParams({ action });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') qs.set(key, String(value));
  });
  return `${apiService.getAdminEndpoint(ADMIN_ENDPOINT)}?${qs}`;
}

async function adminPassportPost(
  action: string,
  body: Record<string, unknown> = {}
): Promise<Record<string, unknown>> {
  const response = await apiService.makeRequest<Record<string, unknown>>(
    apiService.getAdminEndpoint(ADMIN_ENDPOINT),
    {
      method: 'POST',
      body: JSON.stringify({ action, ...body }),
      headers: apiService.getAuthHeaders(),
    }
  );
  if (!response.success) {
    throw new Error(
      response.message || response.error || `Failed to complete admin action: ${action}`
    );
  }
  return response.data ?? {};
}

/** Admin snapshot; shipped keys default on when the API omits a key. */
export async function adminGetPassportFlags(): Promise<PassportFeatureFlags> {
  const response = await apiService.makeRequest<{ flags?: Record<string, boolean> }>(
    adminPassportUrl('flags'),
    {
      headers: apiService.getAuthHeaders(),
    }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load Passport flags');
  }
  const flat = response.data?.flags || {};
  return { ...SHIPPED_PASSPORT_FLAGS, ...flat };
}

export async function adminSetPassportFlag(
  flag: keyof PassportFeatureFlags,
  enabled: boolean
): Promise<void> {
  await adminPassportPost('set_flag', { flag, enabled });
}

/** Review queue = encounters filtered to `pending` (no dedicated queue table). */
export async function adminListPassportReviewQueue(
  status: string = 'pending'
): Promise<PassportReviewQueueItem[]> {
  const response = await apiService.makeRequest<PassportReviewQueueItem[]>(
    adminPassportUrl('encounters', { status, limit: 50 }),
    { headers: apiService.getAuthHeaders() }
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function adminListPassportEncounters(
  filters: {
    subject_type?: string;
    status?: string;
    risk_status?: string;
  } = {}
): Promise<PassportEncounter[]> {
  const response = await apiService.makeRequest<PassportEncounter[]>(
    adminPassportUrl('encounters', { ...filters, limit: 100 }),
    { headers: apiService.getAuthHeaders() }
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function adminPassportReviewDecision(payload: {
  encounter_id: number;
  decision: 'approved' | 'rejected' | 'needs_more_info';
  reason?: string;
}): Promise<void> {
  await adminPassportPost('review', payload);
}

/**
 * There is no per-encounter "revoke" action on the backend today — only
 * `revoke_token` (invalidating the scan credential that produced encounters).
 * Until that ships, the closest available moderation lever is rejecting the
 * encounter, which is what this calls under the hood.
 */
export async function adminRevokePassportEncounter(
  encounterId: number,
  reason: string
): Promise<void> {
  await adminPassportPost('review', { encounter_id: encounterId, decision: 'rejected', reason });
}

export async function adminRevokePassportScanToken(tokenId: number, reason: string): Promise<void> {
  await adminPassportPost('revoke_token', { token_id: tokenId, reason });
}

/**
 * No admin "list all appeals" action exists on the backend yet — appeals
 * currently surface only via `passport_scan_appeal.php` (the submitting
 * member's own list). This call will fail until that ships; callers should
 * treat the failure as an honest "not available yet" state.
 */
export async function adminListPassportAppeals(): Promise<PassportScanAppeal[]> {
  throw new Error('Appeal review queue is not available yet — no admin list endpoint exists.');
}

export async function adminResolvePassportAppeal(payload: {
  public_id: string;
  status: 'upheld_rejection' | 'overturned_approved' | 'withdrawn';
  resolution_notes?: string;
}): Promise<void> {
  await adminPassportPost('appeal_resolve', {
    appeal_public_id: payload.public_id,
    decision: payload.status,
    notes: payload.resolution_notes,
  });
}

export async function getPassportRecap(year?: number): Promise<Record<string, unknown>> {
  const qs = year ? `?year=${encodeURIComponent(String(year))}` : '';
  const response = await apiService.makeRequest<Record<string, unknown>>(
    `/passport_recap.php${qs}`,
    {
      method: 'GET',
      headers: apiService.getAuthHeaders(),
    }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load Passport recap');
  }
  return response.data;
}

export async function listPassportAchievements(): Promise<
  Array<{
    public_id: string;
    name: string;
    description?: string;
    template_key: string;
    short_label?: string;
    issued_at?: string;
    hidden?: boolean;
  }>
> {
  const response = await apiService.makeRequest<{ achievements?: unknown[] }>(
    '/passport_achievements.php',
    {
      method: 'GET',
      headers: apiService.getAuthHeaders(),
    }
  );
  const list = response.data?.achievements;
  return Array.isArray(list) ? (list as any[]) : [];
}

export async function getPassportVisibilitySettings(): Promise<{
  passport_private: boolean;
  collections_public: boolean;
  badges_public: boolean;
  recap_sharing?: boolean;
}> {
  const response = await apiService.makeRequest<{
    passport_private?: boolean;
    collections_public?: boolean;
    badges_public?: boolean;
    recap_sharing?: boolean;
  }>('/passport_visibility_settings.php', {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load privacy settings');
  }
  return {
    passport_private: response.data.passport_private !== false,
    collections_public: Boolean(response.data.collections_public),
    badges_public: Boolean(response.data.badges_public),
    recap_sharing: Boolean(response.data.recap_sharing),
  };
}

export async function updatePassportVisibilitySettings(fields: {
  passport_private?: boolean;
  collections_public?: boolean;
  badges_public?: boolean;
  recap_sharing?: boolean;
}): Promise<void> {
  const response = await apiService.makeRequest('/passport_visibility_settings.php', {
    method: 'POST',
    headers: apiService.getAuthHeaders(),
    body: JSON.stringify(fields),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to save privacy settings');
  }
}

export interface PassportOwnerAnalytics {
  /** Unique collectors whose encounter reached event/proximity/staff verification. */
  seen_in_person_strong: number;
  /** Unique collectors with a basic QR encounter. */
  qr_encounters_basic: number;
  public_count_enabled: boolean;
  suspicious_or_held: number;
  verification_mix: Array<{
    verification_level: PassportVerificationLevel;
    unique_users: number;
    total: number;
  }>;
  daily_30d: Array<{ date: string; unique_users: number; total?: number }>;
  recent: Array<{
    public_id: string;
    verification_level: PassportVerificationLevel;
    occurred_at: string;
    status: PassportEncounterStatus;
    risk_status: PassportRiskStatus;
  }>;
}

/**
 * `passportOwnerVehicleAnalytics()` nests counts under `aggregates` using the public
 * vocabulary (`seen_in_person_strong` / `qr_encounters_basic`). Flattening them here keeps
 * the panel from reading names the endpoint never returns, which silently rendered zeros.
 */
export async function getPassportOwnerAnalytics(
  vehicleId: number
): Promise<PassportOwnerAnalytics> {
  const response = await apiService.makeRequest<{
    aggregates?: {
      seen_in_person_strong?: number;
      qr_encounters_basic?: number;
      public_count_enabled?: boolean;
    };
    suspicious_or_held?: number;
    verification_mix?: PassportOwnerAnalytics['verification_mix'];
    daily_30d?: PassportOwnerAnalytics['daily_30d'];
    recent?: PassportOwnerAnalytics['recent'];
  }>(`/passport_owner_analytics.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`, {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load owner analytics');
  }
  const raw = response.data;
  return {
    seen_in_person_strong: raw.aggregates?.seen_in_person_strong ?? 0,
    qr_encounters_basic: raw.aggregates?.qr_encounters_basic ?? 0,
    public_count_enabled: Boolean(raw.aggregates?.public_count_enabled),
    suspicious_or_held: raw.suspicious_or_held ?? 0,
    verification_mix: Array.isArray(raw.verification_mix) ? raw.verification_mix : [],
    daily_30d: Array.isArray(raw.daily_30d) ? raw.daily_30d : [],
    recent: Array.isArray(raw.recent) ? raw.recent : [],
  };
}

export type PassportSubjectSettings = {
  collection_enabled: boolean;
  public_count_enabled: boolean;
  notifications_enabled: boolean;
  first_discoverer_enabled: boolean;
};

export async function getPassportSubjectSettings(
  subjectType: PassportSubjectType,
  subjectId: number
): Promise<PassportSubjectSettings> {
  const qs = new URLSearchParams({
    subject_type: subjectType,
    subject_id: String(subjectId),
  });
  const response = await apiService.makeRequest<PassportSubjectSettings>(
    `/passport_subject_settings.php?${qs}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load Passport settings');
  }
  return response.data;
}

export async function updatePassportSubjectSettings(
  subjectType: PassportSubjectType,
  subjectId: number,
  fields: Partial<PassportSubjectSettings>
): Promise<PassportSubjectSettings> {
  const response = await apiService.makeRequest<PassportSubjectSettings>(
    '/passport_subject_settings.php',
    {
      method: 'POST',
      headers: apiService.getAuthHeaders(),
      body: JSON.stringify({ subject_type: subjectType, subject_id: subjectId, ...fields }),
    }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to update Passport settings');
  }
  return response.data;
}

export type PassportScanTokenPrintable = {
  scan_url: string | null;
  qr_svg?: string | null;
  qr_svg_inverted?: string | null;
};

export type PassportScanTokenSummary = {
  id: number;
  public_id: string;
  subject_type: PassportSubjectType;
  subject_id: number;
  token_kind: string;
  period_rule: string;
  requires_location: boolean;
  geofence_radius_m: number | null;
  status: 'active' | 'revoked' | 'expired' | string;
  token_fingerprint: string;
  use_count: number;
  max_uses: number | null;
  expires_at: string | null;
  created_at: string;
  revoked_at?: string | null;
  /** Present for shop/club managers when the encrypted secret is stored server-side. */
  printable?: PassportScanTokenPrintable | null;
};

export type PassportIssuedScanToken = {
  token: PassportScanTokenSummary;
  raw_token: string;
  scan_url: string | null;
  qr_svg?: string | null;
  qr_svg_inverted?: string | null;
  print_hint?: string;
  /** Human-readable vinyl footer (shop name, checkpoint title, etc.). */
  sticker_label?: string | null;
  /** False when the encrypted secret was not stored (legacy DB or encryption unavailable). */
  reprint_available?: boolean;
};

export type PassportBuildQr = {
  kind: 'vehicle_profile_qr' | string;
  scan_url: string;
  qr_svg?: string | null;
  qr_svg_inverted?: string | null;
  sticker_label?: string | null;
};

export type PassportSubjectScanTokenList = {
  tokens: PassportScanTokenSummary[];
  build_qr?: PassportBuildQr | null;
  /** Server has token_secret_enc column for shop/club reprint. */
  reprint_supported?: boolean;
  /** Contextual vinyl footer for this subject. */
  sticker_label?: string | null;
};

export async function listPassportSubjectScanTokens(
  subjectType: PassportSubjectType,
  subjectId: number
): Promise<PassportScanTokenSummary[]> {
  const data = await listPassportSubjectScanTokenBundle(subjectType, subjectId);
  return data.tokens;
}

/** List tokens and, for vehicles, the unified build QR (profile URL + printable SVGs). */
export async function listPassportSubjectScanTokenBundle(
  subjectType: PassportSubjectType,
  subjectId: number
): Promise<PassportSubjectScanTokenList> {
  const qs = new URLSearchParams({
    subject_type: subjectType,
    subject_id: String(subjectId),
  });
  const response = await apiService.makeRequest<PassportSubjectScanTokenList>(
    `/passport_subject_scan_token.php?${qs}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to list scan codes');
  }
  return {
    tokens: Array.isArray(response.data?.tokens) ? response.data!.tokens : [],
    build_qr: response.data?.build_qr ?? null,
    reprint_supported: response.data?.reprint_supported === true,
    sticker_label: response.data?.sticker_label ?? null,
  };
}

export async function issuePassportSubjectScanToken(payload: {
  subject_type: PassportSubjectType;
  subject_id: number;
  period_rule?: string;
  token_kind?: string;
  requires_location?: boolean;
  location_mode?: 'off' | 'prefer' | 'require';
  geofence_lat?: number;
  geofence_lng?: number;
  geofence_radius_m?: number;
  max_uses?: number;
  expires_at?: string;
}): Promise<PassportIssuedScanToken> {
  const response = await apiService.makeRequest<PassportIssuedScanToken>(
    '/passport_subject_scan_token.php',
    {
      method: 'POST',
      headers: apiService.getAuthHeaders(),
      body: JSON.stringify({ action: 'issue', ...payload }),
    }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to create scan code');
  }
  return response.data;
}

export async function revokePassportSubjectScanToken(payload: {
  subject_type: PassportSubjectType;
  subject_id: number;
  token_id: number;
  reason?: string;
}): Promise<void> {
  const response = await apiService.makeRequest('/passport_subject_scan_token.php', {
    method: 'POST',
    headers: apiService.getAuthHeaders(),
    body: JSON.stringify({ action: 'revoke', ...payload }),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to revoke scan code');
  }
}

/** Organizer event-hunt facade (`organizer/passport_campaign.php`). */
async function organizerPassportRequest<T>(
  action: string,
  opts: {
    method?: 'GET' | 'POST';
    query?: Record<string, string | number>;
    body?: Record<string, unknown>;
  } = {}
): Promise<T> {
  const qs = new URLSearchParams({ action, ...((opts.query || {}) as Record<string, string>) });
  Object.entries(opts.query || {}).forEach(([k, v]) => qs.set(k, String(v)));
  qs.set('action', action);
  const response = await apiService.makeRequest<T>(`/organizer/passport_campaign.php?${qs}`, {
    method: opts.method || 'GET',
    headers: apiService.getAuthHeaders(),
    body: opts.body ? JSON.stringify({ action, ...opts.body }) : undefined,
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Organizer Passport request failed');
  }
  return response.data as T;
}

export async function organizerListPassportCampaigns(
  eventId: number
): Promise<PassportCampaignSummary[]> {
  const data = await organizerPassportRequest<PassportCampaignSummary[]>('list', {
    query: { event_id: eventId },
  });
  return Array.isArray(data) ? data : [];
}

export async function organizerGetPassportCampaignDetail(
  campaignId: number
): Promise<PassportCampaignSummary> {
  const raw = await organizerPassportRequest<
    PassportCampaignDetailApiShape | PassportCampaignSummary
  >('detail', {
    query: { campaign_id: campaignId },
  });
  if (raw && typeof raw === 'object' && 'campaign' in raw && raw.campaign) {
    const nested = raw as PassportCampaignDetailApiShape;
    return {
      ...nested.campaign,
      objects: nested.objects,
      participation: nested.participant,
    };
  }
  return raw as PassportCampaignSummary;
}

export async function organizerSavePassportCampaign(payload: {
  event_id: number;
  id?: number;
  title: string;
  slug?: string;
  description?: string;
  starts_at?: string;
  ends_at?: string;
  status?: string;
}): Promise<PassportCampaignSummary> {
  const data = await organizerPassportRequest<
    { campaign?: PassportCampaignSummary } | PassportCampaignSummary
  >('save', { method: 'POST', body: payload });
  if (data && typeof data === 'object' && 'campaign' in data && data.campaign) {
    return data.campaign;
  }
  return data as PassportCampaignSummary;
}

export async function organizerAddPassportCampaignObject(payload: {
  campaign_id: number;
  title: string;
  object_kind?: 'booth' | 'activation' | 'destination' | 'checkpoint';
  description?: string;
  sequence_order?: number;
  is_required?: boolean;
  issue_scan_token?: boolean;
  /** off = no GPS; prefer = soft upgrade when nearby; require = must be nearby to check in */
  location_mode?: 'off' | 'prefer' | 'require';
  location_lat?: number;
  location_lng?: number;
  location_radius_m?: number;
}): Promise<{
  id: number;
  public_id: string;
  scan_token?: {
    id: number;
    raw_token: string;
    scan_url: string | null;
    token_fingerprint: string;
    qr_svg?: string | null;
    qr_svg_inverted?: string | null;
  };
}> {
  return organizerPassportRequest('object_add', { method: 'POST', body: payload });
}

export async function organizerUpdatePassportCampaignObject(payload: {
  campaign_id: number;
  object_id: number;
  title?: string;
  description?: string | null;
  is_required?: boolean;
  sequence_order?: number | null;
  location_mode?: 'off' | 'prefer' | 'require';
  location_lat?: number;
  location_lng?: number;
  location_radius_m?: number;
  location_label?: string | null;
}): Promise<PassportCampaignObject & { id: number; status?: string }> {
  return organizerPassportRequest('object_save', { method: 'POST', body: payload });
}

export async function organizerDeletePassportCampaignObject(payload: {
  campaign_id: number;
  object_id: number;
}): Promise<{ id: number; public_id: string; status: string }> {
  return organizerPassportRequest('object_delete', { method: 'POST', body: payload });
}

export async function organizerDeletePassportCampaign(payload: {
  campaign_id: number;
}): Promise<{ id: number; public_id: string; status: string }> {
  return organizerPassportRequest('delete', { method: 'POST', body: payload });
}

export async function adminGetPassportHealth(): Promise<Record<string, unknown>> {
  const response = await apiService.makeRequest<Record<string, unknown>>(
    adminPassportUrl('health'),
    {
      headers: apiService.getAuthHeaders(),
    }
  );
  return (response.data as Record<string, unknown>) || {};
}
